import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Upload, Check } from "lucide-react";
import demoBurger from "@/assets/demo-burger.jpg";
import demoPizza from "@/assets/demo-pizza.jpg";
import demoSmash from "@/assets/demo-smash.jpg";
import demoAcai from "@/assets/demo-acai.jpg";
import demoLimonada from "@/assets/demo-limonada.jpg";
import demoLogo from "@/assets/demo-logo.png";
import demoCover from "@/assets/demo-cover.jpg";

type Phase = "upload" | "analyzing" | "generating" | "done";

const ITEMS = [
  { name: "Burger Artesanal", desc: "Blend de angus 180g, cheddar, bacon crocante.", price: "32,90", img: demoBurger },
  { name: "Smash Burger Duplo", desc: "Dois blends smash, cheddar e cebola caramelizada.", price: "38,90", img: demoSmash },
  { name: "Pizza Margherita", desc: "San Marzano, mozzarella de búfala, manjericão.", price: "40,50", img: demoPizza },
  { name: "Açaí Premium 500ml", desc: "Açaí puro, granola, banana e mel.", price: "22,90", img: demoAcai },
  { name: "Limonada Suíça", desc: "Limão siciliano, leite condensado, cremosa.", price: "12,90", img: demoLimonada },
];

const TOTAL = ITEMS.length;
const LOOP_MS = 13000;

export function HeroPhoneAnimation() {
  const [phase, setPhase] = useState<Phase>("upload");
  const [genCount, setGenCount] = useState(0);
  const [revealedImgs, setRevealedImgs] = useState<number[]>([]);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };

    const run = () => {
      clear();
      setPhase("upload");
      setGenCount(0);
      setRevealedImgs([]);

      const t = (fn: () => void, ms: number) => {
        const id = setTimeout(fn, ms);
        timers.current.push(id);
      };

      t(() => setPhase("analyzing"), 2000);
      t(() => { setPhase("generating"); setGenCount(0); }, 3800);
      for (let i = 1; i <= TOTAL; i++) {
        t(() => setGenCount(i), 3800 + i * 700);
      }
      t(() => { setPhase("done"); setRevealedImgs([]); }, 3800 + TOTAL * 700 + 200);
      for (let i = 0; i < ITEMS.length; i++) {
        t(() => setRevealedImgs((prev) => [...prev, i]), 3800 + TOTAL * 700 + 400 + i * 320);
      }
      t(run, LOOP_MS);
    };

    run();
    return clear;
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden rounded-[1.75rem]" style={{ background: '#fff' }}>
      {/* Cover */}
      <div className="relative h-44 w-full overflow-hidden">
        <img src={demoCover} alt="" className="h-full w-full object-cover" style={{ objectPosition: "center 55%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.3) 100%)" }} />
      </div>

      {/* Restaurant card */}
      <div className="relative mx-3 -mt-9 rounded-xl bg-white p-3 shadow-lg ring-1 ring-black/5">
        <div className="flex items-center gap-2.5">
          <img src={demoLogo} alt="" className="h-11 w-11 rounded-lg object-cover ring-1 ring-black/5" />
          <div className="flex-1 min-w-0">
            <div className="text-[14px] font-bold text-zinc-900 leading-tight truncate">Sabor & Arte Bistrô</div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-px ring-1 ring-emerald-200/60">
                <span className="h-1 w-1 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[8px] font-semibold text-emerald-700">Aberto</span>
              </span>
              <span className="text-[10px] text-zinc-500 truncate">Jardins, SP</span>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay for upload / analyzing phases */}
      <AnimatePresence>
        {(phase === "upload" || phase === "analyzing") && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 flex flex-col items-center justify-center bg-white/95 z-20"
          >
            <AnimatePresence mode="wait">
              {phase === "upload" && (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="flex flex-col items-center gap-4 px-6 text-center"
                >
                  <div className="relative">
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 ring-1 ring-orange-200">
                      <Upload className="h-7 w-7 text-orange-500" />
                    </div>
                    <motion.div
                      className="absolute -right-1 -bottom-1 flex h-6 w-6 items-center justify-center rounded-full bg-orange-500 ring-2 ring-[#fffaf5]"
                      animate={{ scale: [1, 1.15, 1] }}
                      transition={{ repeat: Infinity, duration: 1.2 }}
                    >
                      <Loader2 className="h-3.5 w-3.5 text-white animate-spin" />
                    </motion.div>
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">Enviando cardápio...</p>
                    <p className="mt-0.5 text-[10px] text-zinc-400">cardapio-sabor-arte.jpg</p>
                  </div>
                  <div className="w-32 h-1 rounded-full bg-zinc-100 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-orange-400"
                      initial={{ width: "5%" }}
                      animate={{ width: "90%" }}
                      transition={{ duration: 1.6, ease: "easeInOut" }}
                    />
                  </div>
                </motion.div>
              )}

              {phase === "analyzing" && (
                <motion.div
                  key="analyzing"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.35 }}
                  className="flex flex-col items-center gap-4 px-6 text-center"
                >
                  <motion.div
                    className="flex h-16 w-16 items-center justify-center rounded-full"
                    style={{ background: "linear-gradient(135deg, #FF6A00, #FF8C00)" }}
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 2.5, ease: "linear" }}
                  >
                    <Sparkles className="h-7 w-7 text-white" />
                  </motion.div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">Analisando os pratos</p>
                    <AnimatedDots />
                  </div>
                  <div className="flex gap-1.5">
                    {["Burger", "Pizza", "Açaí", "Bebidas"].map((tag, i) => (
                      <motion.span
                        key={tag}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.2 }}
                        className="rounded-full bg-orange-50 px-2 py-0.5 text-[9px] font-medium text-orange-700 ring-1 ring-orange-200"
                      >
                        {tag}
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating progress pill for generating phase */}
      <AnimatePresence>
        {phase === "generating" && (
          <motion.div
            key="gen-bar"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1.5 rounded-full border border-orange-200 bg-white px-2.5 py-1 shadow-md"
          >
            <Sparkles className="h-3 w-3 text-orange-500 animate-pulse shrink-0" />
            <span className="text-[9px] font-semibold text-zinc-700 whitespace-nowrap">
              Gerando fotos... {genCount}/{TOTAL}
            </span>
            <div className="w-14 h-1 rounded-full bg-zinc-100 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-orange-400"
                animate={{ width: `${(genCount / TOTAL) * 100}%` }}
                transition={{ duration: 0.4 }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category pills */}
      <div className="mt-3 flex flex-wrap gap-1 px-3 pb-0.5">
        {["Lanches", "Pizzas", "Açaí", "Bebidas"].map((c, i) => (
          <span
            key={c}
            className={`shrink-0 rounded-full px-2 py-0.5 text-[8px] font-semibold ${
              i === 0 ? "text-white" : "text-zinc-500 bg-zinc-100"
            }`}
            style={i === 0 ? { background: "#e85d04" } : undefined}
          >
            {c}
          </span>
        ))}
      </div>

      {/* Items */}
      <div className="mt-2 space-y-0 px-3 pb-3">
        {ITEMS.map((item, idx) => {
          const hasImage = phase === "done" && revealedImgs.includes(idx);
          const isSpinning = phase === "generating" && genCount <= idx;
          return (
            <div key={item.name} className="flex items-start gap-2 border-b border-zinc-100 py-1.5 last:border-b-0">
              <div className="relative shrink-0 h-12 w-12 rounded-lg overflow-hidden bg-zinc-100">
                <AnimatePresence>
                  {hasImage ? (
                    <motion.img
                      key="img"
                      src={item.img}
                      alt=""
                      initial={{ opacity: 0, scale: 1.08 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.45 }}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : isSpinning ? (
                    <motion.div
                      key="spin"
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <Loader2 className="h-4 w-4 animate-spin text-orange-400" />
                    </motion.div>
                  ) : phase === "generating" ? (
                    <motion.div
                      key="done-mini"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0"
                    >
                      <img src={item.img} alt="" className="h-full w-full object-cover" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                        <Check className="h-4 w-4 text-white drop-shadow" />
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-1">
                  <span className="text-[10px] font-semibold text-zinc-900 truncate leading-tight">{item.name}</span>
                  {hasImage && (
                    <motion.span
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="shrink-0 flex h-3.5 w-3.5 items-center justify-center rounded-full shadow"
                      style={{ background: "linear-gradient(135deg, #FF6A00, #FF8C00)" }}
                    >
                      <Sparkles className="h-2 w-2 text-white" />
                    </motion.span>
                  )}
                </div>
                <p className="text-[7px] text-zinc-500 leading-snug line-clamp-1">{item.desc}</p>
                <span className="text-[10px] font-extrabold text-zinc-900">R${item.price}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AnimatedDots() {
  return (
    <span className="inline-flex gap-0.5 ml-0.5">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="inline-block h-1 w-1 rounded-full bg-zinc-400"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.2 }}
        />
      ))}
    </span>
  );
}
